import { useState, useEffect, FormEvent, useRef } from 'react';
import { Plus, PersonStanding, Search, Upload, X, ChevronDown } from 'lucide-react';
import { bodyweightApi } from '@/services/api';
import { useUIStore } from '@/store/uiStore';
import { Card, Button, Modal } from '@/components/common';
import { BodyweightGridCard } from '@/components/bodyweight/BodyweightGridCard';
import type { BodyweightExercise, BodyweightSubcategory } from '@/types';

// Bodyweight subcategory options
const subcategoryOptions: { id: BodyweightSubcategory; label: string; icon: string }[] = [
  { id: 'upper', label: 'Felsőtest', icon: '💪' },
  { id: 'lower', label: 'Alsótest', icon: '🦵' },
  { id: 'core', label: 'Törzs', icon: '🎯' },
  { id: 'complex', label: 'Komplex', icon: '🔥' },
];

const Bodyweight = () => {
  const [exercises, setExercises] = useState<BodyweightExercise[]>([]);
  const [muscleGroups, setMuscleGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<BodyweightSubcategory | ''>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState<BodyweightExercise | null>(null);
  const { showToast } = useUIStore();

  const [formName, setFormName] = useState('');
  const [formMuscleGroup, setFormMuscleGroup] = useState('');
  const [formSubcategory, setFormSubcategory] = useState<BodyweightSubcategory>('upper');
  const [formPhotoUrl, setFormPhotoUrl] = useState<string | null>(null);
  const [formSaving, setFormSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadExercises = async () => {
    try {
      const params: { subcategory?: string; search?: string } = {};
      
      if (selectedSubcategory) {
        params.subcategory = selectedSubcategory;
      }
      
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const response = await bodyweightApi.getAll(params);
      if (response.success && response.data) {
        setExercises(response.data.exercises);
      }
    } catch (error) {
      console.error('Failed to load exercises:', error);
      showToast('Hiba a gyakorlatok betöltésekor.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadMuscleGroups = async () => {
    try {
      const response = await bodyweightApi.getMuscleGroups();
      if (response.success && response.data) {
        setMuscleGroups(response.data.muscleGroups);
      }
    } catch (error) {
      console.error('Failed to load muscle groups:', error);
    }
  };

  useEffect(() => {
    loadExercises();
    loadMuscleGroups();
  }, [selectedSubcategory, searchQuery]);

  const openModal = (exercise?: BodyweightExercise) => {
    if (exercise) {
      setEditingExercise(exercise);
      setFormName(exercise.name);
      setFormMuscleGroup(exercise.muscleGroup);
      setFormSubcategory(exercise.subcategory);
      setFormPhotoUrl(exercise.photoUrl || null);
    } else {
      setEditingExercise(null);
      setFormName('');
      setFormMuscleGroup('');
      setFormSubcategory('upper');
      setFormPhotoUrl(null);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingExercise(null);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast('Csak képfájl tölthető fel!', 'error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast('A kép maximum 5MB lehet!', 'error');
      return;
    }

    // For new exercises, we need to create first then upload
    // For existing exercises, we can upload directly
    if (!editingExercise) {
      // Store file for upload after creation
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormPhotoUrl(event.target?.result as string);
      };
      reader.readAsDataURL(file);
      // Store the file for later upload
      (window as any).__pendingExerciseFile = file;
    } else {
      // Upload immediately for existing exercises
      await uploadPhoto(editingExercise.id, file);
    }
  };

  const uploadPhoto = async (exerciseId: string, file: File) => {
    setUploading(true);
    try {
      const response = await bodyweightApi.uploadPhoto(exerciseId, file);

      if (!response.success || !response.data) {
        throw new Error('Failed to upload photo');
      }

      setFormPhotoUrl(response.data.photoUrl);
      showToast('Kép sikeresen feltöltve!', 'success');
      loadExercises();
    } catch (error) {
      console.error('Upload failed:', error);
      showToast('Hiba a kép feltöltésekor.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    if (editingExercise && editingExercise.photoUrl) {
      try {
        await bodyweightApi.update(editingExercise.id, { photoUrl: null });
        setFormPhotoUrl(null);
        showToast('Kép eltávolítva!', 'success');
        loadExercises();
      } catch (error) {
        console.error('Failed to remove photo:', error);
        showToast('Hiba a kép eltávolításakor.', 'error');
      }
    } else {
      setFormPhotoUrl(null);
      (window as any).__pendingExerciseFile = null;
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormSaving(true);

    try {
      if (editingExercise) {
        await bodyweightApi.update(editingExercise.id, {
          name: formName,
          muscleGroup: formMuscleGroup,
          subcategory: formSubcategory,
        });
        showToast('Gyakorlat sikeresen módosítva!', 'success');
      } else {
        // Create exercise first
        const response = await bodyweightApi.create({
          name: formName,
          muscleGroup: formMuscleGroup,
          subcategory: formSubcategory,
        });

        // If we have a pending file, upload it now
        const pendingFile = (window as any).__pendingExerciseFile;
        if (pendingFile && response.success && response.data) {
          await uploadPhoto(response.data.exercise.id, pendingFile);
          (window as any).__pendingExerciseFile = null;
        }

        showToast('Gyakorlat sikeresen hozzáadva!', 'success');
      }
      closeModal();
      loadExercises();
    } catch (error) {
      console.error('Failed to save exercise:', error);
      showToast('Hiba történt a mentés során.', 'error');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async (exercise: BodyweightExercise) => {
    if (!confirm(`Biztosan törlöd a "${exercise.name}" gyakorlatot?`)) return;

    try {
      await bodyweightApi.delete(exercise.id);
      showToast('Gyakorlat sikeresen törölve!', 'success');
      loadExercises();
    } catch (error) {
      console.error('Failed to delete exercise:', error);
      showToast('Hiba történt a törlés során.', 'error');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Saját súlyos gyakorlatok</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base">Saját testsúlyos edzésformák kezelése</p>
        </div>
        <Button size="sm" icon={<Plus className="w-4 h-4" />} onClick={() => openModal()} className="flex-shrink-0 self-start sm:self-auto">
          Új gyakorlat
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400"
            placeholder="Keresés..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="relative">
          <select
            className="w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            value={selectedSubcategory}
            onChange={(e) => setSelectedSubcategory(e.target.value as BodyweightSubcategory | '')}
          >
            <option value="">Minden kategória</option>
            {subcategoryOptions.map((sub) => (
              <option key={sub.id} value={sub.id}>
                {sub.icon} {sub.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Exercises Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : exercises.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <PersonStanding className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Nincs találat</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {searchQuery || selectedSubcategory 
                ? 'Nincs a keresésnek megfelelő gyakorlat.'
                : 'Add hozzá az első saját súlyos gyakorlatot az "Új gyakorlat" gombbal.'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {exercises.map((exercise) => (
            <BodyweightGridCard
              key={exercise.id}
              exercise={exercise}
              onEdit={() => openModal(exercise)}
              onDelete={() => handleDelete(exercise)}
            />
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingExercise ? 'Gyakorlat szerkesztése' : 'Új gyakorlat hozzáadása'}
        size="md"
        footer={
          <>
            <button
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              onClick={closeModal}
            >
              Mégse
            </button>
            <Button type="submit" form="exercise-form" loading={formSaving}>
              Mentés
            </Button>
          </>
        }
      >
        <form id="exercise-form" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Photo Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Gyakorlat képe
              </label>
              <div className="flex items-start gap-4">
                {formPhotoUrl ? (
                  <div className="relative">
                    <img
                      src={formPhotoUrl}
                      alt="Preview"
                      className="w-24 h-24 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                    />
                    <button
                      type="button"
                      onClick={removePhoto}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="w-24 h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                  >
                    {uploading ? (
                      <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-6 h-6 text-gray-400 mb-1" />
                        <span className="text-xs text-gray-500 dark:text-gray-400">Feltöltés</span>
                      </>
                    )}
                  </div>
                )}
                <div className="flex-1 text-sm text-gray-500 dark:text-gray-400">
                  <p>JPG, PNG, WebP</p>
                  <p>Max 5MB</p>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Gyakorlat neve <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="pl. Fekvőtámasz"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Izomcsoport <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                  value={formMuscleGroup}
                  onChange={(e) => setFormMuscleGroup(e.target.value)}
                  required
                >
                  <option value="">Válassz izomcsoportot...</option>
                  {muscleGroups.map((group) => (
                    <option key={group} value={group}>{group}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Kategória <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {subcategoryOptions.map((sub) => (
                  <button
                    key={sub.id}
                    type="button"
                    className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg border-2 transition-all ${
                      formSubcategory === sub.id
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                        : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                    onClick={() => setFormSubcategory(sub.id)}
                  >
                    <span className="text-lg">{sub.icon}</span>
                    <span className="text-sm font-medium">{sub.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Bodyweight;
